import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { StyledTableCellProps as StyledTableCell } from '../../commons/utils/TableStyles';
import { GridEmptyRow } from '../../commons/grid/GridStateRow.jsx';
import * as Utils from '../../commons/utils/Utils.js';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import { etiquetaDeTipo } from './inventarioMovimientosTipos.js';

/**
 * VEC-3315 Scenario 04 — detalle de un movimiento con todas sus líneas.
 *
 * ACÁ EL GRANO SE INVIERTE respecto de la grilla: la grilla muestra líneas sueltas y este modal
 * muestra la cabecera completa con las suyas. Es la única vista donde se ve que un movimiento puede
 * involucrar varios ítems — hoy invisible, porque el ingreso de VEC-3314 escribe una sola línea, y
 * por eso el modal se va a ver casi siempre con una fila. No es un caso degenerado: es el estado del
 * sistema hasta que exista un movimiento multi-ítem.
 *
 * Reusa el permiso del historial (INVENTARIO_STOCK_VISUALIZAR_MOVIMIENTOS) y no pide uno propio: es
 * el mismo dato con otro corte, y la razón por la que ese permiso se separó de LISTAR —expone costos
 * y responsables— aplica igual acá.
 */

const BASE_URL = '/inventario/stock/movimientos';

const COLUMN_COUNT = 4;

const InventarioMovimientoDetalleModal = (props) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [detalle, setDetalle] = useState(null);

  const movimientoId = props.movimientoId;

  useEffect(() => {
    if (!movimientoId) {
      setDetalle(null);
      return;
    }

    setLoading(true);
    Utils.getData(`${BASE_URL}/${movimientoId}`)
      .then((data) => setDetalle(data || null))
      .catch(() => setDetalle(null))
      .finally(() => setLoading(false));
  }, [movimientoId]);

  const formatEntero = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return LocaleFormat.formatNumber(Number(value), 0);
  };

  /** Sin símbolo de moneda: multimoneda es VEC-3325. `null` = ingreso sin precio, distinto de 0. */
  const formatCosto = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return LocaleFormat.formatNumber(Number(value), 2);
  };

  const nombreUbicacion = (u) => (u && u.nombre ? u.nombre : '-');

  /** Un dato de la cabecera: etiqueta arriba, valor abajo. */
  const renderDato = (etiqueta, valor) => (
    <div className="col-md-4 col-12 mb-2">
      <div className="text-muted" style={{ fontSize: 12 }}>
        {etiqueta}
      </div>
      <div>{valor}</div>
    </div>
  );

  const lineas = detalle && detalle.lineas ? detalle.lineas : [];

  return (
    <Dialog
      open={!!movimientoId}
      onClose={props.onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ style: { maxWidth: 1140 } }}
    >
      <DialogTitle disableTypography className="modal-header bg-fleet">
        <h4 className="modal-title text-white mb-0">
          <i className="la la-exchange align-middle icon-modal-title"></i>
          <FormattedMessage id="inventarioMovimientos.detalle.titulo" defaultMessage=" Detalle del movimiento " />
          {detalle ? '#' + detalle.id : ''}
        </h4>
      </DialogTitle>

      <DialogContent dividers className="modal-grey">
        {loading && (
          <div className="text-center my-3">
            <CircularProgress />
          </div>
        )}

        {!loading && !detalle && (
          <div className="text-center my-3">
            <FormattedMessage
              id="inventarioMovimientos.detalle.no_encontrado"
              defaultMessage="No se pudo cargar el detalle de este movimiento."
            />
          </div>
        )}

        {!loading && detalle && (
          <>
            <div className="row">
              {renderDato(
                intl.formatMessage({ id: 'inventarioMovimientos.detalle.fecha', defaultMessage: 'Fecha' }),
                detalle.fecha ? LocaleFormat.formatDate(detalle.fecha) : '-'
              )}
              {renderDato(
                intl.formatMessage({ id: 'inventarioMovimientos.detalle.tipo', defaultMessage: 'Tipo' }),
                etiquetaDeTipo(intl, detalle.tipo)
              )}
              {renderDato(
                intl.formatMessage({ id: 'inventarioMovimientos.detalle.usuario', defaultMessage: 'Responsable' }),
                detalle.usuario || '-'
              )}
              {renderDato(
                intl.formatMessage({ id: 'inventarioMovimientos.detalle.origen', defaultMessage: 'Origen' }),
                nombreUbicacion(detalle.ubicacionOrigen)
              )}
              {renderDato(
                intl.formatMessage({ id: 'inventarioMovimientos.detalle.destino', defaultMessage: 'Destino' }),
                nombreUbicacion(detalle.ubicacionDestino)
              )}
              {renderDato(
                intl.formatMessage({ id: 'inventarioMovimientos.detalle.motivo', defaultMessage: 'Motivo' }),
                detalle.motivo || '-'
              )}
            </div>

            <TableContainer>
              <Table aria-label="inventario movimiento detalle table" className="table-hover">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="left">
                      <FormattedMessage
                        id="inventarioMovimientos.detalle.column_numero_de_parte"
                        defaultMessage="Nº de parte"
                      />
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      <FormattedMessage
                        id="inventarioMovimientos.detalle.column_descripcion"
                        defaultMessage="Descripción"
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <FormattedMessage
                        id="inventarioMovimientos.detalle.column_cantidad"
                        defaultMessage="Cantidad"
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <FormattedMessage
                        id="inventarioMovimientos.detalle.column_costo"
                        defaultMessage="Costo unitario"
                      />
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.length > 0 ? (
                    lineas.map((linea) => (
                      <TableRow key={'DetalleRow_' + linea.id}>
                        <StyledTableCell align="left">
                          {linea.item && linea.item.numeroDeParte ? linea.item.numeroDeParte : '-'}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {linea.item && linea.item.descripcion ? linea.item.descripcion : '-'}
                        </StyledTableCell>
                        <StyledTableCell align="right">{formatEntero(linea.cantidad)}</StyledTableCell>
                        <StyledTableCell align="right">{formatCosto(linea.costo)}</StyledTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <GridEmptyRow
                      columns={COLUMN_COUNT}
                      messageId="inventarioMovimientos.detalle.sin_lineas"
                      label="Este movimiento no tiene líneas."
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions className="modal-grey">
        <button type="button" className="btn btn-default btn-fleet" onClick={props.onClose}>
          <FormattedMessage id="Ok" defaultMessage="Ok" />
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default InventarioMovimientoDetalleModal;
